import { Divider, Flex, useToast } from '@chakra-ui/react';
import { Bookmark, Check, ClipboardPen } from 'lucide-react';
import Image from 'next/image';
import { usePathname } from 'next/navigation';
import React, { useEffect } from 'react';
import { render } from 'storyblok-rich-text-react-renderer';
import { useShallow } from 'zustand/react/shallow';

import { CustomCard as CustomCardStoryblok } from '@/.storyblok/types/225909/storyblok-components';
import CustomToast from '@/common/CustomToast/CustomToast';
import envConfig from '@/env.config';
import { Constants } from '@/lib/constants';
import { useAppStore } from '@/stores/AppStore';
import { useStoryblokStore } from '@/stores/StoryblokStore';
import { useUserInfoStore } from '@/stores/UserInfoStore';
import { convertStringToClassName } from '@/utils/convertStringToClassName';
import { generateCardLink } from '@/utils/generateCardLink';
import './CustomCard.scss';

interface CustomCardProps {
  blok: CustomCardStoryblok;
}

const CustomCard = ({ blok }: CustomCardProps) => {
  //////////////////////////////
  //  Stores
  //////////////////////////////

  const [
    setShowExternalRedirectModal,
    setExternalLinkToOpenInRedirectModal,
    isNewDesign,
    isCollectionEnabled,
    hasScrolled,
  ] = useAppStore(
    useShallow(state => [
      state.setShowExternalRedirectModal,
      state.setExternalLinkToOpenInRedirectModal,
      state.isNewDesign,
      state.isCollectionEnabled,
      state.hasScrolled,
    ])
  );

  const [isLoggedOut, isLoggedInWithoutName] = useUserInfoStore(
    useShallow(state => [state.isLoggedOut, state.isLoggedInWithoutName])
  );

  //////////////////////////////
  // Viewed Assets
  //////////////////////////////

  const [viewedAssets, setViewedAssets, collection, setCollection] =
    useStoryblokStore(
      useShallow(state => [
        state.viewedAssets,
        state.setViewedAssets,
        state.collection,
        state.setCollection,
      ])
    );

  // Effect to load state from localStorage on mount
  useEffect(() => {
    const storedViewedAssets = localStorage.getItem('viewedAssets');

    if (storedViewedAssets) {
      const parsedAssets = JSON.parse(storedViewedAssets);
      setViewedAssets(parsedAssets);
    }
  }, [setViewedAssets]);

  // Effect to save state to localStorage when state changes
  useEffect(() => {
    if (viewedAssets.length > 0) {
      localStorage.setItem('viewedAssets', JSON.stringify(viewedAssets));
    }
  }, [viewedAssets]);

  //////////////////////////////

  const viewedAsset = viewedAssets.includes(blok.CardHeader);
  const isPreview = blok.IsPreview ?? false;
  const isPreviewAndLoggedOut =
    isPreview && (isLoggedOut || isLoggedInWithoutName);

  const description = render(blok.CardSummary);
  const cardMetadataArray = blok.CardMetadataOne?.length
    ? [...blok.CardMetadataOne]
    : [];

  const cardTitleClassName = convertStringToClassName(blok.CardHeader);

  const topicClassName = blok.Topic.map((topic: string) =>
    convertStringToClassName(topic)
  );
  const themeClassName = blok.Theme.map((theme: string | number) =>
    convertStringToClassName(theme.toString())
  );
  const assetTypeClassName = blok.AssetType.map((assetType: string | number) =>
    convertStringToClassName(assetType.toString())
  );
  const industryClassName = blok.Industry.map((industry: string | number) =>
    convertStringToClassName(industry.toString())
  );

  const combinedClassNames = [
    cardTitleClassName,
    ...topicClassName,
    ...themeClassName,
    ...assetTypeClassName,
    ...industryClassName,
  ].join(' ');

  ////////////////////////////
  //  Mark card as viewed
  ////////////////////////////

  const markCardAsViewed = () => {
    const clickedAsset = blok.CardHeader;
    const newViewedAssets = [...viewedAssets, clickedAsset];
    const uniqueViewedAssets = [...new Set(newViewedAssets)];
    setViewedAssets(uniqueViewedAssets);
  };

  ////////////////////////////
  // Styling
  ////////////////////////////

  const hasImage = !!blok.CardImage?.filename;

  const pathname = usePathname(); // '/partners'
  const currentTopicPage = pathname?.split('/')[1]; // 'partners'

  const cardTopicArray = blok.Topic.flatMap((topic: string) => {
    if (isNewDesign) {
      if (topic === 'Find Government Support') return [topic, 'Gov Support']; //
      if (topic === 'Find Partners') return [topic, 'Partners'];
    }

    return topic; // ['Explore Business Opportunities', 'Find Government Support']
  });

  const cardTopic =
    cardTopicArray.find(
      topic => convertStringToClassName(topic) === currentTopicPage
    ) ?? cardTopicArray[0];

  const cardLinkFromStoryblok = blok?.CardLink ?? '';
  const isCardLinkExternal = cardLinkFromStoryblok.startsWith('http');

  const cardLink = generateCardLink(cardTopic, cardLinkFromStoryblok);

  ////////////////////////////
  //  Handle Click
  ////////////////////////////

  const openExternalLink = (url: string) => {
    const shouldShowRedirectModal =
      localStorage.getItem('dontShowExternalRedirectModalAgain') !== 'true';

    if (shouldShowRedirectModal) {
      setShowExternalRedirectModal(true);
      setExternalLinkToOpenInRedirectModal(url);
    } else {
      const openedWindow = window.open(url, '_blank');
      if (openedWindow) {
        markCardAsViewed();
      }
    }
  };

  ////////////////////////////
  //  Redirect Modal
  ////////////////////////////

  const handleClick = () => {
    // For gated assets - to mark asset card as viewed after proceeding in Redirect Modal, which is shown after login
    // Unable to store in global state as it will be lost after login
    if (isLoggedOut) {
      localStorage.setItem('nameOfPageToOpenAfterLogin', blok.CardHeader);
    }

    if (isCardLinkExternal) {
      openExternalLink(cardLink);
      return;
    }

    const openedWindow = window.open(cardLink, '_blank');

    if (openedWindow) {
      markCardAsViewed();
    }
  };

  //////////////////////////////////////////

  const toast = useToast({
    position: 'top-right',
    containerStyle: {
      position: 'relative',
      top: hasScrolled ? '80px' : '120px', // Adjust based on navbar height
      right: '16px',
      width: '328px',
    },
  });

  const isSelected = Array.isArray(collection)
    ? collection.some((item: any) => item.id === blok.id)
    : false;

  const handleToggleCollection = () => {
    const isInCollection = collection.some(item => item.id === blok.id);
    const updatedCollection = isInCollection
      ? collection.filter(item => item.id !== blok.id)
      : [...collection, blok];

    setCollection(updatedCollection);

    // Show custom toast
    toast({
      duration: 3000,
      render: () => (
        <CustomToast
          variant={isInCollection ? 'error' : 'success'}
          icon={isInCollection ? 'bookmark-removed' : 'bookmark-added'}
          description={
            isInCollection ? 'Removed from collection' : 'Added to collection'
          }
        />
      ),
    });
  };

  /////////////////////////////////////////////

  return (
    <Flex
      onClick={handleClick}
      className={`custom-card-outer-container ${combinedClassNames}`}
    >
      <Flex className={`custom-card-container`}>
        <Flex className="custom-card-text-container">
          <Flex className={'custom-card-category-and-header'}>
            <Flex className={'custom-card-category-container'}>
              {blok.AssetType.map(element => (
                <span key={element} className={`custom-card-asset-type`}>
                  {element}
                </span>
              ))}

              {cardMetadataArray.length > 0 && (
                <>
                  <Divider orientation={'vertical'} className={'divider'} />
                  {cardMetadataArray.map((cardMetadata: any, index: number) => {
                    const connectionAvailable =
                      cardMetadata === 'Connection Available';
                    const connectionAvailableClassName = connectionAvailable
                      ? 'connection-available'
                      : '';

                    return (
                      <React.Fragment key={cardMetadata}>
                        <Flex
                          className={`custom-card-metadata ${connectionAvailableClassName}`}
                        >
                          {cardMetadata}
                          {connectionAvailable && (
                            <ClipboardPen
                              className={'connection-available-icon'}
                            />
                          )}
                        </Flex>
                        {index < cardMetadataArray.length - 1 && (
                          <Divider
                            orientation={'vertical'}
                            className={'divider'}
                          />
                        )}
                      </React.Fragment>
                    );
                  })}
                </>
              )}
            </Flex>

            <Flex className={'custom-card-header'}>{blok.CardHeader}</Flex>
          </Flex>

          <Flex className={'custom-card-description'}>
            <span>{description}</span>
          </Flex>

          <Flex className={'custom-card-footer'}>
            {viewedAsset && (
              <>
                <Check />
                Viewed
              </>
            )}

            {viewedAsset && isPreviewAndLoggedOut && (
              <Divider orientation={'vertical'} className={'divider'} />
            )}

            {isPreviewAndLoggedOut && (
              <span>{Constants.TopicPage.AssetCard.Preview}</span>
            )}
          </Flex>
        </Flex>

        {hasImage && (
          <Flex className={'custom-card-image-container'}>
            <Image
              src={blok.CardImage!.filename as string}
              alt={'Card Image'}
              width={120}
              height={160}
              className={'custom-card-image'}
              quality={envConfig.internal.image.quality}
            />
          </Flex>
        )}
      </Flex>

      {isCollectionEnabled && (
        <Flex
          className={`custom-card-bookmark ${isSelected ? 'selected' : ''}`}
          onClick={e => {
            e.stopPropagation(); // prevent card click
            handleToggleCollection();
          }}
        >
          <Bookmark
            className={`custom-card-bookmark-icon ${
              isSelected ? 'selected' : ''
            }`}
          />
        </Flex>
      )}
    </Flex>
  );
};

export default CustomCard;
